import RightOutlined from "@ant-design/icons/RightOutlined";
import {
  Avatar,
  Box,
  ClickAwayListener,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Popper,
  Typography,
  useMediaQuery,
} from "@mui/material";
// material-ui
import { useTheme } from "@mui/material/styles";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import Transitions from "@/components/@extended/Transitions";
// project import
import MainCard from "@/components/MainCard";
import useUserData from "@/hooks/useUserData";
import { getInitials, initialsAvatarSx } from "@/pages/utils/avatarDisplay";
import { logError } from "@/utils/logging";

import { ExpandMore } from "./ExpandMore";
import ProfileMenu from "./ProfileMenu";
import { logout } from "./utils/logout";

interface ProfileProps {
  drawerOpen: boolean;
}

const Profile = ({ drawerOpen }: ProfileProps) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  // On mobile the drawer is a temporary Modal; below lg we must portal the menu out.
  const matchDownLG = useMediaQuery(theme.breakpoints.down("lg"));
  const anchorRef = useRef<HTMLDivElement>(null);
  const { userProfile } = useUserData();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogout = async () => {
    try {
      await logout(dispatch);
      navigate("/auth/login");
    } catch (error) {
      logError("Sign out error", error instanceof Error ? error.message : "Unknown error");
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  return (
    <Box
      sx={{
        borderTop: drawerOpen ? "1px solid" : "none",
        borderTopColor: "#D8D8D8",
        // pl: drawerOpen ? "0px" : "4px",
        pl: "8px",
        pr: "2px",
        pb: !drawerOpen ? "24px" : "0px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          p: drawerOpen ? "24px 15px" : 0,

          cursor: "pointer",
        }}
        onClick={handleToggle}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? "profile-grow" : undefined}
        aria-haspopup="true"
      >
        {drawerOpen && (
          <List disablePadding>
            <ListItem
              disablePadding
              secondaryAction={
                <ExpandMore
                  theme={theme}
                  expand={open}
                  id="basic-button"
                  aria-controls={open ? "basic-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  aria-label="show more"
                >
                  <RightOutlined style={{ fontSize: "12px", color: "#BFBFBF" }} />
                </ExpandMore>
              }
              sx={{ "& .MuiListItemSecondaryAction-root": { right: -8 } }}
            >
              <ListItemAvatar>
                <Avatar
                  alt="Avatar"
                  src={userProfile?.avatar}
                  sx={{ width: 45, height: 45, ...initialsAvatarSx(45) }}
                >
                  {getInitials(userProfile?.companyName)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                sx={{
                  width: "163px",
                  mt: 0,
                  mb: 0,
                }}
                primary={
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{
                      color: "#262626",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {userProfile?.companyName}
                  </Typography>
                }
                secondary={
                  <Typography variant="h6" color="secondary">
                    Administrator
                  </Typography>
                }
              />
            </ListItem>
          </List>
        )}
        {!drawerOpen && (
          <Avatar
            alt="Avatar"
            src={userProfile?.avatar}
            sx={{ width: 60, height: 60, mx: "auto", ...initialsAvatarSx(60) }}
          >
            {getInitials(userProfile?.companyName)}
          </Avatar>
        )}
      </Box>
      <Popper
        placement={drawerOpen ? "top-end" : "top-start"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        // Desktop uses a permanent drawer, so keep the menu inline (disablePortal) where
        // it positions correctly. Mobile uses a temporary Modal drawer whose subtree
        // offsets the fixed menu (it floated near the page bottom), so portal it to the
        // body there to anchor against the viewport.
        disablePortal={!matchDownLG}
        popperOptions={{
          // Position relative to the viewport. Previously the strategy defaulted to
          // "absolute" while the sx forced `position: fixed`, so Popper's computed
          // offsets were interpreted against the wrong origin. "fixed" also lets the
          // menu escape the collapsed mini-drawer's overflow clipping on desktop.
          strategy: "fixed",
          modifiers: [
            {
              name: "preventOverflow",
              enabled: false,
            },
            {
              name: "offset",
              options: {
                offset: drawerOpen ? [-8, -14] : [16, -14],
              },
            },
          ],
        }}
        // Stack above the drawer's temporary Modal (theme.zIndex.modal === 1300) on mobile.
        sx={{ zIndex: theme.zIndex.modal + 1 }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            {open && (
              <Paper
                sx={{
                  boxShadow: theme.customShadows.z1,
                  width: 170,
                }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <MainCard elevation={0} border={false} content={false}>
                    <ProfileMenu handleLogout={handleLogout} closeMenu={() => setOpen(false)} />
                  </MainCard>
                </ClickAwayListener>
              </Paper>
            )}
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Profile;
